import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  LOCALE_ID,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import { environment } from '../environments/environment';

import { routes } from './app.routes';
import {
  AuthRedirectLoopService,
  buildAccessDeniedRedirectUrl,
  clearCurrentUrlQueryParams,
  providePublicAuthConfig,
} from '@auth';
import { provideShareAuthSession } from '@share-auth-session';
import {
  HttpClient,
  provideHttpClient,
  withInterceptors,
} from '@angular/common/http';
import { Observable, take } from 'rxjs';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { AuthService } from './services/auth.service';
import { UserSettingsService } from './services/user-settings.service';
import { AuthActions } from './store/state/auth/auth.action-types';
import { authenticationInterceptor } from './services/authentication-interceptor.service';
import {
  TranslateLoader,
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';
import { provideUiTranslations } from '@ui';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { Directionality } from '@angular/cdk/bidi';
import { LayoutModule } from '@angular/cdk/layout';
import { CustomDirectionality } from '@ui';
import { provideAppState } from './store/app.state.config';
import { provideShareOrganizationSettings } from '@share-organization-settings';
import { provideShareUserAccountSettings } from '@share-user-account-settings';
import { api, userManagerURL } from './consts/url';
import { routerSegments } from './consts/routes';
import { provideToastr } from 'ngx-toastr';
import {
  ParkingInvalidSessionHandler,
  ParkingSharedAuthSessionManager,
} from './services/shared-auth-session.service';
import { UniversalLoginService } from './services/universal-login.service';
import {
  createBrowserAnalyticsConfig,
  provideAnalytics,
} from '@share-analytics';

export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  const version = new Date().getTime();
  return new TranslateHttpLoader(http, './assets/i18n/', `.json?v=${version}`);
}

export function initApp(): Observable<unknown> {
  const store = inject(Store);
  const actions$ = inject(Actions);
  const authService = inject(AuthService);
  const universalLoginService = inject(UniversalLoginService);
  const authRedirectLoopService = inject(AuthRedirectLoopService);

  return new Observable<unknown>((subscriber) => {
    // Subscribe FIRST so we can't miss the success action
    const sub = actions$
      .pipe(
        ofType(AuthActions.requiredDataLoadedSuccess, AuthActions.logout),
        take(1),
      )
      .subscribe({
        next: () => {
          subscriber.next(void 0);
          subscriber.complete();
        },
        error: (err) => subscriber.error(err),
      });

    const currentUrl = new URL(window.location.href);
    const error = currentUrl.searchParams.get('error');
    const errorDescription = currentUrl.searchParams.get('error_description');

    if (
      currentUrl.pathname === `/${routerSegments.emailVerification}`
      && currentUrl.searchParams.get('success') === 'true'
    ) {
      clearCurrentUrlQueryParams(currentUrl);
      universalLoginService.redirectToLogin(window.location.origin);
      subscriber.next(void 0);
      subscriber.complete();
      return () => sub.unsubscribe();
    }

    if (error === 'access_denied') {
      const redirectUrl = buildAccessDeniedRedirectUrl({
        error,
        errorDescription,
        resetPasswordPath: `/${routerSegments.resetPassword}`,
        emailVerificationPath: `/${routerSegments.emailVerification}`,
        accessDeniedPath: `/${routerSegments.accessDenied}`,
      });

      if (redirectUrl) {
        clearCurrentUrlQueryParams(currentUrl);
        window.location.href = redirectUrl;
        subscriber.next(void 0);
        subscriber.complete();
        return () => sub.unsubscribe();
      }

      authService.logout({ msg: errorDescription ?? error });
      subscriber.next(void 0);
      subscriber.complete();
      return () => sub.unsubscribe();
    }

    if (authService.isLogoutCallbackRoute(currentUrl)) {
      authRedirectLoopService.reset();
      authService.handleLogoutCallback(currentUrl);
      subscriber.next(void 0);
      subscriber.complete();
      return () => sub.unsubscribe();
    }

    const code = currentUrl.searchParams.get('code');

    if (code && isRootRoute(currentUrl.pathname)) {
      authRedirectLoopService.reset();
      currentUrl.searchParams.delete('code');
      currentUrl.searchParams.delete('state');
      currentUrl.searchParams.delete('orgId');
      window.history.replaceState({}, '', currentUrl.toString());
      store.dispatch(
        AuthActions.socialLoginByCode({ code }),
      );
      return () => sub.unsubscribe();
    }

    // Then do the bootstrap logic (dispatches / navigation)
    if (!authService.isLoggedIn()) {
      store.dispatch(AuthActions.requiredDataLoadedSuccess());
      // Don't block initializer on navigate promise; routing will proceed after bootstrap
      // void router.navigate([routerSegments.auth]);
    } else {
      authRedirectLoopService.reset();
      store.dispatch(AuthActions.startLoadRequiredData());
    }

    return () => sub.unsubscribe();
  });
}

function isRootRoute(pathname: string): boolean {
  return pathname === '' || pathname === '/';
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideHttpClient(withInterceptors([authenticationInterceptor])),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideAnimations(),
    provideRouter(routes),
    provideToastr(),
    ...provideAnalytics(createBrowserAnalyticsConfig(environment.env)),
    providePublicAuthConfig({
      auth0Domain: environment.auth0.domain,
      auth0ClientId: environment.auth0.clientID,
      authStartUrl: api.authV2.login,
      logoutPublicUrl: api.authV2.logoutPublic,
      resendVerificationEmailPublicUrl: api.authV2.resendVerificationEmailPublic,
    }),
    {
      provide: LOCALE_ID,
      deps: [TranslateService],
      useFactory: (translateService: TranslateService) =>
        translateService.getCurrentLang() || 'en-US',
    },
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
        fallbackLang: 'en',
        /**
         * IMPORTANT TO KEEP ALL TRANSLATIONS WITH LIBS
         */
        extend: true,
      }),
      LayoutModule,
    ),
    {
      provide: Directionality,
      useExisting: CustomDirectionality,
    },
    provideAppInitializer(initApp),
    ...provideShareAuthSession({
      sessionManager: ParkingSharedAuthSessionManager,
      invalidSessionHandler: ParkingInvalidSessionHandler,
    }),
    ...provideUiTranslations(),
    ...provideShareOrganizationSettings(),
    ...provideShareUserAccountSettings({
      apiUrl: environment.apiLumana,
      userManagerUrl: userManagerURL,
    }),
    UserSettingsService,
    provideAppState(),
  ],
};
